import { useState, useEffect } from 'react';

/**
 * Breakpoint constants (match Tailwind's breakpoints)
 * Also used in e2e/fixtures.js for test viewport detection
 */
export const BREAKPOINTS = {
  sm: 640,
  md: 768, // Primary mobile/desktop breakpoint
  lg: 1024,
  xl: 1280,
} as const;

/**
 * Custom hook for responsive design with media queries
 *
 * Cross-browser compatibility:
 * - Chrome: Full support
 * - Firefox: Full support
 * - Safari 14+: addEventListener support
 * - Safari < 14: Uses deprecated addListener fallback
 * - iOS Safari: Same as Safari desktop
 *
 * @param query - CSS media query string (e.g., '(min-width: 768px)')
 * @returns boolean indicating if the media query matches
 */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState<boolean>(() => {
    // Check if window is available (SSR safety)
    if (typeof window !== 'undefined') {
      return window.matchMedia(query).matches;
    }
    return false;
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const mediaQueryList = window.matchMedia(query);

    const listener = (event: MediaQueryListEvent): void => {
      setMatches(event.matches);
    };

    // Ensure the state is correct on mount and listen for changes
    setMatches(mediaQueryList.matches);

    // Safari < 14 doesn't support addEventListener on MediaQueryList
    // Use addListener as fallback (deprecated but still works)
    if (typeof mediaQueryList.addEventListener === 'function') {
      mediaQueryList.addEventListener('change', listener);
    } else if (typeof mediaQueryList.addListener === 'function') {
      // Deprecated but needed for older Safari
      mediaQueryList.addListener(listener);
    }

    return () => {
      if (typeof mediaQueryList.removeEventListener === 'function') {
        mediaQueryList.removeEventListener('change', listener);
      } else if (typeof mediaQueryList.removeListener === 'function') {
        // Deprecated but needed for older Safari
        mediaQueryList.removeListener(listener);
      }
    };
  }, [query]);

  return matches;
}

/**
 * Convenience hook for checking if viewport is large desktop size (1280px+)
 * Uses xl breakpoint so tablets (including iPad Pro at 1024px) use mobile/tablet layout
 * @returns boolean indicating if viewport is large desktop size
 */
export function useIsDesktop(): boolean {
  return useMediaQuery(`(min-width: ${BREAKPOINTS.xl}px)`);
}

/**
 * Convenience hook for checking if viewport is large desktop size (1024px+)
 * @returns boolean indicating if viewport is large desktop size
 */
export function useIsLargeDesktop(): boolean {
  return useMediaQuery(`(min-width: ${BREAKPOINTS.lg}px)`);
}

/**
 * Convenience hook for checking if the viewport is 768px or wider: where a
 * page's heading band draws its Scene art. Below it the art is not mounted
 * at all, so a phone never downloads it (hiding it with CSS still loads an
 * eager image). False before a window exists.
 * @returns boolean indicating if viewport is 768px or wider
 */
export function useIsTabletUp(): boolean {
  return useMediaQuery(`(min-width: ${BREAKPOINTS.md}px)`);
}

/**
 * Convenience hook for checking if viewport is mobile size (< 768px)
 * @returns boolean indicating if viewport is mobile size
 */
export function useIsMobile(): boolean {
  return useMediaQuery(`(max-width: ${BREAKPOINTS.md - 1}px)`);
}

export default useMediaQuery;
